const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE = baseEnvironment.replace(/\/$/, '');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const parseResponse = async (response, fallbackMessage) => {
  const payload = await response.json().catch(() => null);
  if (response.ok) {
    return payload;
  }
  const message = payload?.detail || payload?.message || fallbackMessage || `Request failed (${response.status})`;
  const error = new Error(message);
  error.status = response.status;
  error.payload = payload;
  throw error;
};

const buildQuery = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value));
    }
  });
  return query.toString();
};

export async function runIfrsBatch({ companyId, periodId, ruleSetVersion, calculationEngineVersion, strictMode = false }) {
  const response = await fetch(`${API_BASE}/api/ifrs/batches/run`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({ companyId, periodId, ruleSetVersion, calculationEngineVersion, strictMode })
  });
  return parseResponse(response, 'IFRS batch calistirilamadi.');
}

export async function getIfrsBatch({ batchId, companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/batches/${batchId}?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS batch detayi alinamadi.');
}

export async function listIfrsBatches({ companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/batches?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS batch listesi alinamadi.');
}

export async function approveIfrsBatch({ batchId, companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/batches/${batchId}/approve?${query}`, {
    method: 'POST',
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS batch onaylanamadi.');
}

export async function submitIfrsBatchForReview({ batchId, companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/batches/${batchId}/submit-review?${query}`, {
    method: 'POST',
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS batch reviewe gonderilemedi.');
}

export async function rejectIfrsBatch({ batchId, companyId, periodId, reason }) {
  const query = buildQuery({ companyId, periodId, reason });
  const response = await fetch(`${API_BASE}/api/ifrs/batches/${batchId}/reject?${query}`, {
    method: 'POST',
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS batch reddedilemedi.');
}

export async function restateIfrsBatch({ batchId, companyId, periodId, reason }) {
  const query = buildQuery({ companyId, periodId, reason });
  const response = await fetch(`${API_BASE}/api/ifrs/batches/${batchId}/restate?${query}`, {
    method: 'POST',
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS batch restatement olusturulamadi.');
}

export async function fetchIfrsMappingCompleteness({ batchId, companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/batches/${batchId}/mapping-completeness?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'Mapping completeness alinamadi.');
}

export async function fetchIfrsTrialBalance({ companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/ledger/trial-balance?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS trial balance alinamadi.');
}

export async function fetchIfrsReport({ companyId, periodId, reportType }) {
  if (reportType === 'vukIfrsComparison') {
    const query = buildQuery({ companyId, periodId });
    const detailedResponse = await fetch(`${API_BASE}/api/ifrs/comparison/detailed?${query}`, {
      headers: buildHeaders()
    });
    if (detailedResponse.ok) {
      const payload = await detailedResponse.json().catch(() => null);
      return {
        batchId: payload?.sessionId ?? null,
        batchVersion: 'WORKSHEET_V2',
        ruleSetVersion: '1.0',
        calculationEngineVersion: '1.0',
        reportName: 'VUK_IFRS_COMPARISON',
        status: 'READY',
        sourceTrace: 'ifrs_statement_lines',
        note: null,
        comparisonFormat: 'worksheetDetailed',
        lines: Array.isArray(payload?.lines) ? payload.lines : []
      };
    }
    if (detailedResponse.status !== 412) {
      return parseResponse(detailedResponse, 'IFRS raporu alinamadi.');
    }
  }

  const reportPathMap = {
    financialPosition: 'financial-position',
    profitLossOci: 'profit-loss-oci',
    cashFlow: 'cash-flow',
    equityChanges: 'equity-changes',
    notes: 'notes',
    vukIfrsComparison: 'vuk-ifrs-comparison'
  };
  const endpoint = reportPathMap[reportType];
  if (!endpoint) {
    throw new Error('Gecersiz IFRS rapor tipi.');
  }
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/reports/${endpoint}?${query}`, {
    headers: buildHeaders()
  });
  const payload = await parseResponse(response, 'IFRS raporu alinamadi.');
  if (reportType === 'vukIfrsComparison') {
    return {
      ...payload,
      comparisonFormat: 'auditLegacy'
    };
  }
  return payload;
}

export async function fetchCompanyPeriods({ companyId }) {
  const query = buildQuery({ companyId });
  const response = await fetch(`${API_BASE}/api/periods?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'Donemler alinamadi.');
}

export async function fetchIfrsReconciliation({ companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/reconciliation/vuk-ifrs?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS mutabakat raporu alinamadi.');
}

export async function fetchIfrsReconciliationBridge({ companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/reconciliation/bridge?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS bridge raporu alinamadi.');
}

export async function fetchIfrsLineTrace({ companyId, periodId, ifrsLineItem }) {
  const query = buildQuery({ companyId, periodId, ifrsLineItem });
  const response = await fetch(`${API_BASE}/api/ifrs/reconciliation/trace?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS line trace alinamadi.');
}

export async function fetchIfrsBatchDiff({ companyId, periodId, batchAId, batchBId }) {
  const query = buildQuery({ companyId, periodId, batchAId, batchBId });
  const response = await fetch(`${API_BASE}/api/ifrs/reconciliation/batch-diff?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS batch diff alinamadi.');
}

export async function fetchIfrsXbrlExport({ companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/export/xbrl?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'XBRL export bilgisi alinamadi.');
}

export async function listCashflowRules({ companyId, includeDefaults = true, includeInactive = false }) {
  const query = buildQuery({ companyId, includeDefaults, includeInactive });
  const response = await fetch(`${API_BASE}/api/ifrs/config/cashflow-rules?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'Cash flow kurallari alinamadi.');
}

export async function fetchCashflowPolicy({ companyId }) {
  const query = buildQuery({ companyId });
  const response = await fetch(`${API_BASE}/api/ifrs/config/cashflow-rules/policy?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'Cash flow policy alinamadi.');
}

export async function updateCashflowPolicy({ companyId, payload }) {
  const query = buildQuery({ companyId });
  const response = await fetch(`${API_BASE}/api/ifrs/config/cashflow-rules/policy?${query}`, {
    method: 'PUT',
    headers: buildHeaders(),
    body: JSON.stringify(payload)
  });
  return parseResponse(response, 'Cash flow policy guncellenemedi.');
}

export async function createCashflowRule({ companyId, payload }) {
  const query = buildQuery({ companyId });
  const response = await fetch(`${API_BASE}/api/ifrs/config/cashflow-rules?${query}`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(payload)
  });
  return parseResponse(response, 'Cash flow kurali olusturulamadi.');
}

export async function updateCashflowRule({ companyId, ruleId, payload }) {
  const query = buildQuery({ companyId });
  const response = await fetch(`${API_BASE}/api/ifrs/config/cashflow-rules/${ruleId}?${query}`, {
    method: 'PUT',
    headers: buildHeaders(),
    body: JSON.stringify(payload)
  });
  return parseResponse(response, 'Cash flow kurali guncellenemedi.');
}

export async function deactivateCashflowRule({ companyId, ruleId }) {
  const query = buildQuery({ companyId });
  const response = await fetch(`${API_BASE}/api/ifrs/config/cashflow-rules/${ruleId}?${query}`, {
    method: 'DELETE',
    headers: buildHeaders()
  });
  if (response.ok) {
    return;
  }
  const payload = await response.json().catch(() => null);
  throw new Error(payload?.detail || payload?.message || 'Cash flow kurali pasiflestirilemedi.');
}

export async function fetchIfrsConsolidationFull({ companyId, periodId, groupId }) {
  const query = buildQuery({ companyId, periodId, groupId });
  const response = await fetch(`${API_BASE}/api/ifrs/consolidation/reports/full?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS full consolidation alinamadi.');
}

export async function fetchIfrsWorkflowStatus({ companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/workflow/status?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS workflow durumu alinamadi.');
}

export async function advanceIfrsWorkflow({ companyId, periodId }) {
  const response = await fetch(`${API_BASE}/api/ifrs/workflow/advance`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({ companyId, periodId })
  });
  return parseResponse(response, 'IFRS workflow ilerletilemedi.');
}

export async function resetIfrsWorkflow({ companyId, periodId }) {
  const response = await fetch(`${API_BASE}/api/ifrs/workflow/reset`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({ companyId, periodId })
  });
  return parseResponse(response, 'IFRS workflow sifirlanamadi.');
}

export async function clearIfrsData({ companyId, periodId }) {
  const response = await fetch(`${API_BASE}/api/ifrs/workflow/clear-ifrs-data`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({ companyId, periodId })
  });
  return parseResponse(response, 'IFRS verileri temizlenemedi.');
}

export async function fetchIfrsReadiness({ companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/readiness?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'IFRS readiness alinamadi.');
}

export async function generateVukSnapshot({ companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/vuk-snapshot/generate?${query}`, {
    method: 'POST',
    headers: buildHeaders()
  });
  return parseResponse(response, 'VUK snapshot olusturulamadi.');
}

export async function fetchVukSnapshot({ companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/vuk-snapshot?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'VUK snapshot alinamadi.');
}

export async function fetchVukIfrsComparisonSummary({ companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/comparison/vuk-ifrs-summary?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'VUK-IFRS karsilastirma ozeti alinamadi.');
}

export async function fetchVukIfrsComparisonLines({ companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/comparison/vuk-ifrs-lines?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'VUK-IFRS karsilastirma satirlari alinamadi.');
}

export async function listCollectorDataset({ path, companyId, periodId }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/data-collector/${path}?${query}`, {
    headers: buildHeaders()
  });
  return parseResponse(response, 'Collector verisi alinamadi.');
}

export async function addCollectorDataset({ path, companyId, periodId, payload }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/data-collector/${path}?${query}`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(payload)
  });
  return parseResponse(response, 'Collector verisi eklenemedi.');
}

export async function updateCollectorDataset({ path, companyId, periodId, id, payload }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/data-collector/${path}/${id}?${query}`, {
    method: 'PUT',
    headers: buildHeaders(),
    body: JSON.stringify(payload)
  });
  return parseResponse(response, 'Collector verisi guncellenemedi.');
}

export async function deleteCollectorDataset({ path, companyId, periodId, id }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/data-collector/${path}/${id}?${query}`, {
    method: 'DELETE',
    headers: buildHeaders()
  });
  if (response.ok) {
    return;
  }
  const payload = await response.json().catch(() => null);
  throw new Error(payload?.detail || payload?.message || 'Collector verisi silinemedi.');
}

export async function updateCollectorStatus({ path, companyId, periodId, id, qualityStatus }) {
  const query = buildQuery({ companyId, periodId });
  const response = await fetch(`${API_BASE}/api/ifrs/data-collector/${path}/status?${query}`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({ id, qualityStatus })
  });
  return parseResponse(response, 'Collector status guncellenemedi.');
}

export async function downloadIfrsReportExport({ companyId, periodId, reportType, format }) {
  const query = buildQuery({ companyId, periodId, reportType });
  const token = sessionStorage.getItem('authToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const response = await fetch(`${API_BASE}/api/ifrs/export/reports/${format}?${query}`, {
    headers
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.detail || payload?.message || 'Rapor export alinamadi.');
  }
  return response.blob();
}

export async function downloadComparisonExport({ companyId, periodId, format }) {
  const query = buildQuery({ companyId, periodId });
  const token = sessionStorage.getItem('authToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const response = await fetch(`${API_BASE}/api/ifrs/export/comparison/${format}?${query}`, {
    headers
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.detail || payload?.message || 'Karsilastirma export alinamadi.');
  }
  return response.blob();
}

const ifrsService = {
  runIfrsBatch,
  listIfrsBatches,
  getIfrsBatch,
  approveIfrsBatch,
  submitIfrsBatchForReview,
  rejectIfrsBatch,
  restateIfrsBatch,
  fetchIfrsMappingCompleteness,
  fetchIfrsTrialBalance,
  fetchIfrsReport,
  fetchCompanyPeriods,
  fetchIfrsReconciliation,
  fetchIfrsReconciliationBridge,
  fetchIfrsLineTrace,
  fetchIfrsBatchDiff,
  fetchIfrsXbrlExport,
  fetchIfrsConsolidationFull,
  fetchIfrsWorkflowStatus,
  advanceIfrsWorkflow,
  resetIfrsWorkflow,
  clearIfrsData,
  fetchIfrsReadiness,
  generateVukSnapshot,
  fetchVukSnapshot,
  fetchVukIfrsComparisonSummary,
  fetchVukIfrsComparisonLines,
  listCollectorDataset,
  addCollectorDataset,
  updateCollectorDataset,
  deleteCollectorDataset,
  updateCollectorStatus,
  downloadIfrsReportExport,
  downloadComparisonExport,
  listCashflowRules,
  fetchCashflowPolicy,
  updateCashflowPolicy,
  createCashflowRule,
  updateCashflowRule,
  deactivateCashflowRule
};

export default ifrsService;
